import moment from 'moment';
import posthog from 'posthog-js';

import { STUDENT_SESSION_TYPE_KEYS } from '../../../types/hubEnums';
import { FEATURE_FLAG_NAMES } from '../../legacy/constants/enums';
import { PAUSE_ALLOWED, Session } from '../../legacy/types';
import { SessionPolicy } from '../../legacy/types';
import { SchoolHolidayTerm } from '../../legacy/types';

/* 
  Users can start to pause as early as 14 days before the term ends/holiday starts 
  This is a long-time existing behavior
*/
export const PAUSE_PERIOD_BEFORE_HOLIDAY_START = 0; // TBD-6967

export const isPauseAllowedForHoliday = (enrolmentPolicy?: SessionPolicy, holiday?: SchoolHolidayTerm): boolean => {
  if (!enrolmentPolicy || !enrolmentPolicy?.pause) {
    return false;
  } else if (enrolmentPolicy.pause.pauseAllowed === PAUSE_ALLOWED.ANYTIME) {
    return true;
  } else {
    return enrolmentPolicy.pause.pauseAllowed.split(';').includes(holiday?.type || '');
  }
};

export const getPauseWindow = (holiday: SchoolHolidayTerm): { minDate: moment.Moment; maxDate: moment.Moment } => {
  const holidayStartDate = moment(holiday?.startDate);
  const holidayEndDate = moment(holiday?.endDate);
  const minDate = moment(holidayStartDate).subtract(PAUSE_PERIOD_BEFORE_HOLIDAY_START, 'days');
  const maxDate = moment(holidayEndDate);

  return { minDate, maxDate };
};

export const isWithinPauseWindow = (holiday: SchoolHolidayTerm): boolean => {
  const today = moment();
  const { minDate, maxDate } = getPauseWindow(holiday);

  return today.isBetween(minDate, maxDate, 'day', '[]');
};

export const calculateEarlyPauseStartDate = (endOfTerm?: string, paymentUnits?: number): string | null => {
  if (!endOfTerm || !paymentUnits) {
    return null;
  }
  const endOfTermDate = moment(endOfTerm);
  const leadTime = paymentUnits ? paymentUnits / 2 : null;
  const startDate = endOfTermDate.subtract(leadTime, 'weeks').utc().format();
  return startDate;
};

export const canPauseEnrolment = (enrolmentPolicy: SessionPolicy, holiday: SchoolHolidayTerm): boolean => {
  if (enrolmentPolicy?.pause?.pauseAllowed === PAUSE_ALLOWED.ANYTIME) {
    return true;
  } else {
    return isPauseAllowedForHoliday(enrolmentPolicy, holiday) && isWithinPauseWindow(holiday);
  }
};

// #TODO add unit tests
export const isEligibleForSummerDiscount = (session: Session, holiday: SchoolHolidayTerm) => {
  if (!holiday?.startDate || !holiday?.endDate || !moment(session?.startTime).isValid()) return false;
  const tomorrow = moment().add(1, 'day');
  return (
    // session is within the holiday
    moment(session.startTime).isBetween(moment(holiday.startDate), moment(holiday.endDate), 'day', '[]') &&
    session.studentSessionType === STUDENT_SESSION_TYPE_KEYS.SUMMER_HOLIDAY &&
    session.paymentStatus !== 'Paid' &&
    // and is not within 24 hours
    moment(session.startTime).isAfter(tomorrow, 'hours')
  );
};

// #TODO add unit tests
export const formatSessionSchedule = (session: Session, timezone?: string) => {
  const _timezone = timezone || moment.tz.guess();

  const startTimeFormat = 'ddd D MMMM YYYY';

  const startTime = moment(session.startTime).tz(_timezone);

  return startTime.format(startTimeFormat);
};

export const formatSessionScheduleWithTime = (session: Session, timezone?: string, showTimezone = false) => {
  const _timezone = timezone || moment.tz.guess();

  const startTimeFormat = 'ddd D MMMM YYYY h:mma';
  const endTimeFormat = showTimezone ? 'h:mma (UTCZ)' : 'h:mma';

  const startTime = moment(session.startTime).tz(_timezone);
  const endTime = moment(session.endTime).tz(_timezone);

  return `${startTime.format(startTimeFormat)}-${endTime.format(endTimeFormat)}`;
};

export const isSummerPausePromoEnabled = () => {
  return posthog.isFeatureEnabled(FEATURE_FLAG_NAMES.SUMMER_PAUSE_PROMO);
};

export const isSummerCancelPromoEnabled = () => {
  return posthog.isFeatureEnabled(FEATURE_FLAG_NAMES.SUMMER_CANCEL_PROMO);
};
